import { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useIteroClinics } from '../../../../hooks/useIteroConnection';
import { iteroErrorMessage } from '../../../../services/iteroConnection';
import { ModalShell } from '../../../shared/ModalShell';
import { IteroClinicPicker } from './IteroClinicPicker';
import { selectedClinicText } from './iteroClinicSearch';
import {
    EMPTY_CLINICS_TEXT,
    canConnectTo,
    clinicListView,
    currentClinicId,
    sharingNote,
    type IteroClinicListView
} from './iteroConnectionCopy';

interface IteroConnectModalProps {
    practiceId: string;
    isPending: boolean;
    connectError: unknown;
    onConnect: (clinicId: number) => void;
    onCancel: () => void;
}

const TITLE_ID = 'connect-itero-title';
// One height for every state so the modal never jumps; 48px is ModalShell's overlay padding.
const PANEL_CLASS = 'rounded-xl p-6 flex flex-col h-[min(580px,calc(100vh-48px))]';

function Spinner({ className }: { className: string }) {
    return <span className={`rounded-full border-2 animate-spin ${className}`} />;
}

interface ClinicsStatusProps {
    view: Exclude<IteroClinicListView, 'list'>;
    loadError: unknown;
    onRetry: () => void;
}

function ClinicsStatus({ view, loadError, onRetry }: ClinicsStatusProps) {
    if (view === 'loading') {
        return (
            <div className="flex items-center gap-2.5 text-sm text-gray-600" role="status">
                <Spinner className="h-4 w-4 border-gray-300 border-t-gray-600" />
                Logging in to iTero…
            </div>
        );
    }
    if (view === 'error') {
        return (
            <div className="flex flex-col items-center gap-2 text-center">
                <p className="text-sm text-red-600">
                    {iteroErrorMessage(loadError, "Couldn't load iTero clinics")}
                </p>
                <button
                    type="button"
                    onClick={onRetry}
                    className="px-3 py-1.5 text-[13px] font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    Try again
                </button>
            </div>
        );
    }
    return <p className="text-sm text-gray-600 text-center">{EMPTY_CLINICS_TEXT}</p>;
}

interface ConnectFooterProps {
    selectedText: string | null;
    isPending: boolean;
    canConnect: boolean;
    onConnect: () => void;
    onCancel: () => void;
}

function ConnectFooter({
    selectedText,
    isPending,
    canConnect,
    onConnect,
    onCancel
}: ConnectFooterProps) {
    return (
        <div className="flex justify-end items-center gap-3 mt-6">
            {selectedText && (
                <p className="mr-auto min-w-0 truncate text-sm text-gray-600" title={selectedText}>
                    {selectedText}
                </p>
            )}
            <button
                type="button"
                onClick={onCancel}
                disabled={isPending}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 shrink-0"
            >
                Cancel
            </button>
            <button
                type="button"
                onClick={onConnect}
                disabled={!canConnect}
                className="px-4 py-2 text-sm font-semibold text-white bg-gray-800 rounded-lg hover:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 shrink-0"
            >
                {isPending ? (
                    <>
                        <Spinner className="w-3.5 h-3.5 border-white/30 border-t-white" />
                        Connecting…
                    </>
                ) : (
                    'Connect'
                )}
            </button>
        </div>
    );
}

export function IteroConnectModal({
    practiceId,
    isPending,
    connectError,
    onConnect,
    onCancel
}: IteroConnectModalProps) {
    const clinicsQuery = useIteroClinics(practiceId, true);
    const [pickedId, setPickedId] = useState<number | null>(null);

    const clinics = clinicsQuery.data ?? [];
    const view = clinicListView({
        isLoading: clinicsQuery.isPending || clinicsQuery.isFetching,
        isError: clinicsQuery.isError,
        clinics: clinicsQuery.data
    });
    const selectedId = pickedId ?? currentClinicId(clinics);
    const selectedClinic = clinics.find((clinic) => clinic.clinicId === selectedId);
    const note = view === 'list' ? sharingNote(selectedClinic, practiceId) : null;
    const canConnect = view === 'list' && !isPending && canConnectTo(clinics, selectedId);

    const handleConnect = () => {
        if (selectedId !== null && canConnect) onConnect(selectedId);
    };

    return (
        <ModalShell
            widthClassName="max-w-[520px]"
            panelClassName={PANEL_CLASS}
            dismissible={!isPending}
            onDismiss={onCancel}
            labelledBy={TITLE_ID}
        >
            <div className="flex justify-between items-center mb-4 shrink-0">
                <h3 id={TITLE_ID} className="text-lg font-semibold text-gray-900">
                    Connect to iTero
                </h3>
                <button
                    type="button"
                    onClick={onCancel}
                    disabled={isPending}
                    className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
                    aria-label="Close"
                >
                    <XMarkIcon className="w-5 h-5" />
                </button>
            </div>

            <IteroClinicPicker
                clinics={clinics}
                selectedId={selectedId}
                disabled={isPending}
                onSelect={setPickedId}
                statusContent={
                    view === 'list' ? null : (
                        <ClinicsStatus
                            view={view}
                            loadError={clinicsQuery.error}
                            onRetry={() => clinicsQuery.refetch()}
                        />
                    )
                }
            />

            <div className="shrink-0">
                {note && (
                    <p className="mt-3 text-[12px] text-gray-700 bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
                        {note}
                    </p>
                )}
                {Boolean(connectError) && !isPending && (
                    <p className="mt-3 text-sm text-red-600" role="alert">
                        {iteroErrorMessage(connectError, "Couldn't connect to iTero")}
                    </p>
                )}

                <ConnectFooter
                    selectedText={view === 'list' ? selectedClinicText(selectedClinic) : null}
                    isPending={isPending}
                    canConnect={canConnect}
                    onConnect={handleConnect}
                    onCancel={onCancel}
                />
            </div>
        </ModalShell>
    );
}
